import { useQuery } from '@apollo/client/react';
import { usePathname, useRouter } from 'next/navigation';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Link from '~/shared/components/link';
import { getUserMenuItems } from '~/shared/configs/menus';
import { type Locale, PAGE_NAMES } from '~/shared/constants';
import { notificationUnreadCountQuery } from '~/shared/graphql-queries/notifications';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { checkIsAdminPanel, checkIsClientsPanel, checkIsPlatform } from '~/shared/helpers/check-helpers';
import { useTypedTranslation } from '~/shared/hooks';
import type { ShortUserType } from '~/shared/types/user-type';

const CLOSE_DELAY = 320;

type AvatarValue =
  | string
  | Readonly<{
      path?: string | null;
    }>
  | null
  | undefined;

export type WebMenuUser = Readonly<{
  _id?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  avatar?: AvatarValue;
}>;

type WebMenuItem = Readonly<{
  key: string;
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
}>;

type UseWebMenuBarProps = Readonly<{
  lng: Locale;
  user: ShortUserType | null;
  onSignOutCallback?: () => void;
}>;

const useMenuBar = ({ onSignOutCallback, lng, user }: UseWebMenuBarProps) => {
  const t = useTypedTranslation(['page', 'text']);
  const router = useRouter();
  const isAuthenticated = Boolean(user?._id);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const pathname = usePathname();

  const isAdminPanel = checkIsAdminPanel(pathname);
  const isPlatform = checkIsPlatform(pathname);

  const isClientsPanel = checkIsClientsPanel(pathname);
  const clearCloseTimer = useCallback((): void => {
    if (closeTimerRef.current !== null) {
      globalThis.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback((): void => {
    clearCloseTimer();
    setIsOpen(true);
  }, [clearCloseTimer]);

  const closeMenu = useCallback((): void => {
    clearCloseTimer();
    setIsOpen(false);
  }, [clearCloseTimer]);

  const scheduleCloseMenu = useCallback((): void => {
    clearCloseTimer();

    closeTimerRef.current = globalThis.setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (!containerRef.current?.contains(target)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeMenu]);

  const menuItems = useMemo<readonly WebMenuItem[]>(() => {
    if (!isAuthenticated) {
      return [
        {
          label: (
            <Link
              className="menu-bar__link"
              itemProp="url"
              href={getLocaleLink(isClientsPanel ? PAGE_NAMES.CLIENTS_SIGN_IN : PAGE_NAMES.SIGN_IN, lng)}
            >
              {t('page.sign-in.menu-item')}
            </Link>
          ),
          key: getLocaleLink(isClientsPanel ? PAGE_NAMES.CLIENTS_SIGN_IN : PAGE_NAMES.SIGN_IN, lng),
        },
        {
          label: (
            <Link
              className="menu-bar__link"
              itemProp="url"
              href={getLocaleLink(isClientsPanel ? PAGE_NAMES.CLIENTS_SIGN_UP : PAGE_NAMES.SIGN_UP, lng)}
            >
              {t('page.sign-up.menu-item')}
            </Link>
          ),
          key: getLocaleLink(isClientsPanel ? PAGE_NAMES.CLIENTS_SIGN_UP : PAGE_NAMES.SIGN_UP, lng),
        },
      ];
    }

    return getUserMenuItems({
      isAuthenticated,
      isClientsPanel,
      lng,
      t,
      router,
      closeMenu,
      onSignOut: onSignOutCallback,
      isAdminPanel,
      isPlatform,
      user,
    });
  }, [isAuthenticated, isClientsPanel, isAdminPanel, isPlatform, lng, router, t, user, closeMenu, onSignOutCallback]);

  return {
    isAuthenticated,
    menuItems,
    pathname,
    containerRef,
    isOpen,
    scheduleCloseMenu,
    closeMenu,
    openMenu,
  };
};

export default useMenuBar;
